import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import { getEcho } from './echo';
import type { MessageData, MessageThreadData } from '@/types/api';

const THREADS_KEY = ['messages', 'threads'] as const;
const THREAD_KEY = (number: string) => ['messages', 'thread', number];

async function fetchThreads(filter?: string): Promise<MessageThreadData[]> {
  const { data } = await api.get<MessageThreadData[]>('/v1/messages/threads', {
    params: filter ? { filter } : undefined,
  });
  return data;
}

export function useMessageThreads(filter?: 'deleted') {
  return useQuery({
    queryKey: [...THREADS_KEY, filter ?? 'live'],
    queryFn: () => fetchThreads(filter),
    refetchInterval: 60_000, // fallback; Reverb pushes updates live
  });
}

/** Total unread inbound texts across every thread — drives the sidebar badge. */
export function useUnreadMessagesCount(enabled = true) {
  return useQuery({
    queryKey: [...THREADS_KEY, 'live'],
    queryFn: () => fetchThreads(),
    enabled,
    refetchInterval: 60_000, // fallback; Reverb pushes updates live
    refetchIntervalInBackground: false,
    select: (threads) => threads.reduce((sum, t) => sum + t.unread_count, 0),
  });
}

export function useThread(number: string | null, filter?: 'deleted') {
  return useQuery({
    queryKey: [...THREAD_KEY(number ?? ''), filter ?? 'live'],
    enabled: number != null,
    refetchInterval: number != null ? 60_000 : false, // fallback; Reverb pushes live
    queryFn: async (): Promise<MessageData[]> => {
      const { data } = await api.get<MessageData[]>('/v1/messages', {
        params: { number, ...(filter ? { filter } : {}) },
      });
      return data;
    },
  });
}

/** Soft delete one text. Its MMS media stays on Twilio until the trash is emptied. */
export function useDeleteMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/messages/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages'] }),
  });
}

/**
 * Delete a whole conversation. A thread is not a row, it is every message to or
 * from one number, so this is a bulk delete rather than a cascade.
 */
export function useDeleteThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (number: string): Promise<number> => {
      await primeCsrf();
      const { data } = await api.delete<{ deleted: number }>('/v1/messages/thread', {
        params: { number },
      });
      return data.deleted;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages'] }),
  });
}

export function useRestoreThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (number: string): Promise<void> => {
      await primeCsrf();
      await api.post('/v1/messages/thread/restore', null, { params: { number } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages'] }),
  });
}

/** Empties the trash for good, asking Twilio to delete each message on its side. */
export function usePurgeMessages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<number> => {
      await primeCsrf();
      const { data } = await api.delete<{ purged: number }>('/v1/messages/trashed');
      return data.purged;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages'] }),
  });
}

export type SendMessageInput = { to?: string; contact_id?: number; body: string };

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SendMessageInput): Promise<MessageData> => {
      await primeCsrf();
      const { data } = await api.post<MessageData>('/v1/messages', input);
      return data;
    },
    onSuccess: (message) => {
      qc.invalidateQueries({ queryKey: THREADS_KEY });
      qc.invalidateQueries({ queryKey: THREAD_KEY(message.to_number) });
    },
  });
}

/**
 * Subscribe to the admin channel and refresh message queries the instant a
 * text is received/sent/updated. No-ops (leaving polling in charge) when Reverb
 * isn't reachable. Mount once, high in the admin tree.
 */
export function useMessagesRealtime(enabled = true) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    const echo = getEcho();
    if (!echo) return;

    const channel = echo.private('admin');
    channel.listen('.message', (payload: { thread?: string }) => {
      qc.invalidateQueries({ queryKey: THREADS_KEY });
      if (payload?.thread) {
        qc.invalidateQueries({ queryKey: THREAD_KEY(payload.thread) });
      }
    });

    return () => {
      channel.stopListening('.message');
      echo.leave('admin');
    };
  }, [enabled, qc]);
}

export function useMarkThreadRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (number: string): Promise<void> => {
      await primeCsrf();
      await api.post('/v1/messages/read', { number });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: THREADS_KEY }),
  });
}
